// NOTICE: KAPTION-INTEGRATION
// template-gallery-rumo.* — what the template gallery needs to list Rumo
// templates and per-user favorites for one inbox. Shared by the "Nuevo
// mensaje" wizard and the reply-box "Plantilla" picker, so both open the same
// gallery. Everything is off unless KAPTION_TEMPLATE_GALLERY_RUMO is on.
import { computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import {
  allowedTemplateLanguages,
  defaultTemplateLanguage,
} from 'dashboard/helper/kaptionTemplateLanguages';
import {
  latestRumoVersion,
  templateKey,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';

export const RUMO_GALLERY_FLAG = 'KAPTION_TEMPLATE_GALLERY_RUMO';
// ui_settings key: { [inboxId]: ['template_name::language', …] }
export const FAVORITES_SETTING = 'kaption_template_favorites';

export function useKaptionRumoTemplates({
  inboxId,
  templates,
  conversationLanguage,
}) {
  const store = useStore();
  const { locale } = useI18n();
  const { uiSettings, updateUISettings } = useUISettings();
  const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

  const { canUseFeature } = useFeaturePermissions();

  const enabled = computed(() => isKaptionFlagEnabled(RUMO_GALLERY_FLAG));
  // feature-permissions.REPLY_BOX.10 — admins can hide the Rumo templates per
  // scope; favorites and the rest of the gallery stay.
  const rumoAllowed = computed(
    () => enabled.value && canUseFeature('rumo_templates')
  );
  const inbox = computed(() =>
    unref(inboxId) ? store.getters['inboxes/getInbox'](unref(inboxId)) : null
  );
  const account = computed(() =>
    store.getters['accounts/getAccount'](store.getters.getCurrentAccountId)
  );

  // template-gallery-rumo.LANG.1 — the conversation's language when there is
  // one, else the language the agent uses the dashboard in, else the
  // account's; always one the account allows.
  const rumo = computed(() => {
    if (!rumoAllowed.value) return null;
    const languages = allowedTemplateLanguages(account.value);
    return {
      version:
        inbox.value?.kaption_template_version ||
        latestRumoVersion(unref(templates) || []) ||
        'v2',
      languages,
      language: defaultTemplateLanguage({
        conversationLanguage: unref(conversationLanguage) || locale.value,
        accountLocale: account.value?.locale,
        allowed: languages,
      }),
    };
  });

  // template-gallery-rumo.FAVORITES.1 — per agent and per inbox, kept in the
  // agent's UI settings so they follow them across devices.
  const favorites = computed(() => {
    if (!enabled.value || !unref(inboxId)) return [];
    const stored = uiSettings.value?.[FAVORITES_SETTING] || {};
    return Array.isArray(stored[unref(inboxId)]) ? stored[unref(inboxId)] : [];
  });

  const isFavorite = template =>
    favorites.value.includes(templateKey(template));

  const toggleFavorite = template => {
    if (!enabled.value || !unref(inboxId)) return;
    const key = templateKey(template);
    const next = isFavorite(template)
      ? favorites.value.filter(item => item !== key)
      : [...favorites.value, key];
    updateUISettings({
      [FAVORITES_SETTING]: {
        ...(uiSettings.value?.[FAVORITES_SETTING] || {}),
        [unref(inboxId)]: next,
      },
    });
  };

  return { enabled, rumo, favorites, isFavorite, toggleFavorite };
}
